import { WizardStep } from "../wizard/wizardStep";
import { useIsMobile } from "@/hooks/use-mobile";

export function useCharterWizardSteps(): WizardStep[] {
    const isMobile = useIsMobile();

    return [
        {
            stepId: 1,
            stepName: "Project Name",
            stepDescription: "Enter a Project Name",
            isCompleted: false,
            stepComponentId: "projectName",
            displaySide: isMobile ? "bottom" : "right",
            defaultValue: "New Project"
        },
        {
            stepId: 2,
            stepName: "Project Objective:",
            stepDescription: getObjectiveBasicInfo(),
            isCompleted: false,
            stepComponentId: "objectives",
            displaySide: isMobile ? "bottom" : "right",
            moreInfo: getObjectiveMoreInfo(),

        },
        {
            stepId: 3,
            stepName: "In Scope",
            stepDescription: getScopeBasicInfo(),
            isCompleted: false,
            stepComponentId: "inScope",
            displaySide: isMobile ? "bottom" : "right",
            moreInfo: getScopeMoreInfo(),
        },
        {
            stepId: 4,
            stepName: "Out of Scope",
            stepDescription: getOutOfScopeBasicInfo(),
            isCompleted: false,
            stepComponentId: "outOfScope",
            displaySide: isMobile ? "top" : "right",
            moreInfo: getOutOfScopeMoreInfo(),
        },
        {
            stepId: 5,
            stepName: "Project Sponsor(s)",
            stepDescription: getSponsorBasicInfo(),
            moreInfo: getSponsorMoreInfo(),
            isCompleted: false,
            stepComponentId: "projectSponsors",
            displaySide: isMobile ? "top" : "left",
        },
        {
            stepId: 6,
            stepName: "Project Management",
            stepDescription: getProjectManagementBasicInfo(),
            isCompleted: false,
            stepComponentId: "projectManagers",
            displaySide: isMobile ? "top" : "left",
            moreInfo: getProjectManagementMoreInfo(),
        },
        {
            stepId: 7,
            stepName: "Project Team",
            stepDescription: getProjectTeamBasicInfo(),
            moreInfo: getProjectTeamMoreInfo(),
            isCompleted: false,
            stepComponentId: "projectTeam",
            displaySide: isMobile ? "top" : "left",
        },
        {
            stepId: 8,
            stepName: "Critical Success Factors",
            stepDescription: getCriticalSuccessFactorsBasicInfo(),
            isCompleted: false,
            stepComponentId: "criticalSuccessFactors",
            displaySide: isMobile ? "top" : "left",
            moreInfo: getCriticalSuccessFactorsMoreInfo(),
        },
        {
            stepId: 9,
            stepName: "High Level Timeline",
            stepDescription: "Click here to go to the Plan page and build your project plan. Add tasks from your plan to your timeline by selecting display on charter on the task",
            isCompleted: false,
            stepComponentId: "highLevelTimeline",
            displaySide: isMobile ? "top" : "left",
        }
    ];

    function getObjectiveBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[240px] text-center mx-auto"}>
                <p>Provide <b>Specific, Measurable, Timebound</b> objectives </p>
            </div>
        );
    }

    function getObjectiveMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <p>Objectives should be:</p>
                <ul className="list-disc list-inside">
                    <li><strong>Specific:</strong> Clearly define what you want to achieve.</li>
                    <li><strong>Measurable:</strong> Include criteria to track progress and success.</li>
                    <li><strong>Achievable:</strong> Set realistic goals that can be accomplished.</li>
                    <li><strong>Relevant:</strong> Ensure objectives align with broader business goals.</li>
                    <li><strong>Timebound:</strong> Establish a clear timeline for completion.</li>
                </ul>
            </div>
        );
    }

    function getScopeBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[300px] text-center mx-auto"}>
                <p>Be specific about your scope<br />
                    List all products & processes <b>you <u>will</u> change</b></p>
            </div>
        );
    }

    function getScopeMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <p className="text-bold">Provide the specific scope boundaries of what is <u>In Scope</u></p>
                <p>To do this think about the objective and major deliverables of your project,
                    <b> what areas of the business will you impact?</b>
                </p>
                <br />
                <b>Consider:</b>
                <ul className="list-disc list-inside">
                    <li>What <strong>Products</strong> are in scope? Be specific & list all in scope</li>
                    <li>What <strong>Processes</strong> are in scope? Be specific & list all in scope</li>
                    <li>What <strong>Business Areas</strong> are in scope? Be specific & list all in scope</li>
                </ul>
                <br />
                <b>Consider finishing with an underlying catch all statement:</b>
                <ul>
                    <li>"The products and processes specifically listed in this section are the only products and processes within scope"</li>
                </ul>
            </div>
        );
    }

    function getOutOfScopeBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[300px] text-center mx-auto"}>
                <p>Be specific about your scope<br />
                    List all products & processes that are <br />
                    <b>NOT IN SCOPE</b>
                </p>
            </div>
        );
    }

    function getOutOfScopeMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <p className="text-bold">Provide the specific areas that are <u>Out of Scope</u></p>
                <p>
                    To do this think about the products and processes adjacent to your project scope.
                    The out of scope section is critical to ensure focused effort from your team and to manage your stakeholders expectations
                </p>
                <b>Consider:</b>
                <ul className="list-disc list-inside">
                    <li>Are you impacting an entire product family or a sub-set of a product family?</li>
                    <li>Are you impacting specific process steps or an end to end process?</li>
                </ul>
                <b>Consider finishing with an underlying catch all statement:</b>
                <ul className="list-disc list-inside">
                    <li>Any products or processes not specifically listed in the <b>In Scope</b> section of this document are <b>Out of Scope</b></li>
                </ul>
            </div>
        );
    }

    function getSponsorBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[300px] text-center mx-auto"}>
                <p>Click here to add your Project Sponsors</p>
            </div>
        );
    }

    function getSponsorMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <ul className="list-disc list-inside">
                    <li>Who owns the project outcome?</li>
                    <li>Who's budget is paying for its execution?</li>
                    <li>Who decides on the scope?</li>
                </ul>
                <p>These are your project sponsors - Add these
                    names to this section </p>
            </div>
        );
    }

    function getProjectManagementBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[300px] text-center mx-auto"}>
                <p>Click here to add your Project Management team</p>
            </div>
        );
    }

    function getProjectManagementMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <ul className="list-disc list-inside">
                    <li>Who is responsible for the successful project delivery?</li>
                    <li>Who will lead the team during each phase of the project?</li>
                    <li>Who will lead project tracking, reporting and communications?</li>
                </ul>
                <p>It is critical that you have one name accountable for the delivery of
                    the end to end project – Add this name to this section</p>
            </div>
        );
    }

    function getProjectTeamBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[300px] text-center mx-auto"}>
                <p>Click here to add your Project Team members</p>
            </div>
        );
    }

    function getProjectTeamMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <ul className="list-disc list-inside">
                    <li>Who will deliver the project?</li>
                    <li>Who are responsible for the execution of the tasks to achieve
                        the project deliverables?</li>
                    <li>Who will provide progress updates to the project manager?</li>
                </ul>
                <p>List the names of the people who will do the work required to
                    achieve the project objective</p>
            </div>
        );
    }

    function getCriticalSuccessFactorsBasicInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full text-center mx-auto" : "w-[300px] text-center mx-auto"}>
                <p>List what you need to be successful</p>
            </div>
        );
    }

    function getCriticalSuccessFactorsMoreInfo(): JSX.Element {
        return (
            <div className={isMobile ? "w-full" : "w-[500px]"}>
                <ul className="list-disc list-inside">
                    <li className="text-bold">List the elements you need to be successful</li>
                    <li>Your project sponsor is responsible for ensuring you get what you need to be successful</li>
                </ul>
                <b>Consider the following:</b>
                <ul className="list-disc list-inside">
                    <li>List the critical data inputs you will need, such as, specifications, process maps, protocols, etc.</li>
                    <li>List any access rights you may need, such as, access to specific drives or folders</li>
                    <li>List required business decisions you will need to maintain your track to success</li>
                    <li>List any dependencies you have on other projects ongoing across the business</li>
                </ul>
            </div>
        );
    }
}